import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ShieldCheck, Users, Calendar, Trophy, Loader2, ArrowRight } from 'lucide-react';
interface LoginPageProps {
  onLogin: () => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const validate = () => {
    const newErrors = {
      email: '',
      password: ''
    };
    let isValid = true;
    if (!email) {
      newErrors.email = 'E-Mail-Adresse ist erforderlich';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
      isValid = false;
    }
    if (!password) {
      newErrors.password = 'Passwort ist erforderlich';
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = 'Das Passwort muss mindestens 6 Zeichen lang sein';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setIsLoading(true);
      // Simulate API call
      setTimeout(() => {
        setIsLoading(false);
        onLogin();
      }, 1500);
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const features = [{
    icon: Users,
    title: 'Exklusives Partner-Netzwerk',
    desc: 'Vernetzen Sie sich mit Sponsoren und Entscheidern auf Augenhöhe.'
  }, {
    icon: Calendar,
    title: 'VIP-Events & Ticketing',
    desc: 'Verwalten Sie Einladungen und Zusagen zentral an einem Ort.'
  }, {
    icon: Trophy,
    title: 'Sponsoring & Gamification',
    desc: 'Steigern Sie das Engagement Ihrer Partner durch smarte Challenges.'
  }, {
    icon: ShieldCheck,
    title: 'DSGVO-konform & Sicher',
    desc: 'Höchste Datensicherheit und Privatsphäre für Ihren Verein.'
  }];
  return <div className="min-h-screen flex w-full bg-slate-50 font-sans" data-id="element-225">
      {/* Left Panel - Branding & Features (Hidden on Mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-12 flex-col justify-between relative overflow-hidden" data-id="element-226">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-227">
          <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-emerald-500/10 blur-[120px]" data-id="element-228" />
          <div className="absolute bottom-[10%] -right-[20%] w-[60%] h-[60%] rounded-full bg-blue-500/10 blur-[100px]" data-id="element-229" />
        </div>

        <div className="relative z-10" data-id="element-230">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7
        }} className="flex items-center gap-3 mb-16" data-id="element-231">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30" data-id="element-232">
              <Users className="text-white" size={24} data-id="element-233" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight" data-id="element-234">
              Connectra
            </span>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="max-w-md" data-id="element-235">
            <h1 className="text-4xl font-bold text-white mb-6 leading-tight" data-id="element-236">
              Das digitale Zuhause für Ihr Business-Netzwerk.
            </h1>
            <p className="text-slate-300 text-lg mb-12 leading-relaxed" data-id="element-237">
              Verbinden Sie Sponsoren, Partner und Vereinsmitglieder in einer
              exklusiven, professionellen Umgebung.
            </p>

            <div className="space-y-8" data-id="element-238">
              {features.map((feature, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4 + idx * 0.1
            }} className="flex items-start gap-4" data-id="element-239">
                  <div className="p-2.5 bg-white/10 rounded-lg backdrop-blur-sm border border-white/5" data-id="element-240">
                    <feature.icon className="text-emerald-400" size={20} data-id="element-241" />
                  </div>
                  <div data-id="element-242">
                    <h3 className="text-white font-semibold mb-1" data-id="element-243">
                      {feature.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed" data-id="element-244">
                      {feature.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        <div className="relative z-10 text-slate-500 text-sm" data-id="element-245">
          &copy; {new Date().getFullYear()} Connectra. Alle Rechte vorbehalten.
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 relative" data-id="element-246">
        {/* Mobile Logo (Only visible on small screens) */}
        <div className="absolute top-8 left-8 flex lg:hidden items-center gap-2" data-id="element-247">
          <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center shadow-md" data-id="element-248">
            <Users className="text-white" size={18} data-id="element-249" />
          </div>
          <span className="text-xl font-bold text-slate-900 tracking-tight" data-id="element-250">
            Connectra
          </span>
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8 sm:p-10" data-id="element-251">
          <motion.div variants={itemVariants} className="text-center mb-8" data-id="element-252">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-253">
              Willkommen zurück
            </h2>
            <p className="text-slate-500 text-sm" data-id="element-254">
              Bitte melden Sie sich mit Ihren Zugangsdaten an.
            </p>
          </motion.div>

          <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-5" noValidate data-id="element-255">
            {/* Email Field */}
            <div data-id="element-256">
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-257">
                E-Mail-Adresse
              </label>
              <div className="relative" data-id="element-258">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-259">
                  <Mail className="h-5 w-5 text-slate-400" data-id="element-260" />
                </div>
                <input id="email" type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                if (errors.email) setErrors({
                  ...errors,
                  email: ''
                });
              }} className={`w-full pl-11 pr-4 py-2.5 bg-slate-50 border ${errors.email ? 'border-red-300 focus:ring-red-500/50 focus:border-red-500' : 'border-slate-200 focus:ring-emerald-500/50 focus:border-emerald-500'} rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 transition-colors`} placeholder="name@unternehmen.de" data-id="element-261" />
              </div>
              {errors.email && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1" data-id="element-262">
                  <span className="w-1 h-1 rounded-full bg-red-500 inline-block" data-id="element-263" />
                  {errors.email}
                </p>}
            </div>

            {/* Password Field */}
            <div data-id="element-264">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-265">
                Passwort
              </label>
              <div className="relative" data-id="element-266">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-267">
                  <Lock className="h-5 w-5 text-slate-400" data-id="element-268" />
                </div>
                <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => {
                setPassword(e.target.value);
                if (errors.password) setErrors({
                  ...errors,
                  password: ''
                });
              }} className={`w-full pl-11 pr-12 py-2.5 bg-slate-50 border ${errors.password ? 'border-red-300 focus:ring-red-500/50 focus:border-red-500' : 'border-slate-200 focus:ring-emerald-500/50 focus:border-emerald-500'} rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 transition-colors`} placeholder="••••••••" data-id="element-269" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors" tabIndex={-1} data-id="element-270">
                  {showPassword ? <EyeOff className="h-5 w-5" data-id="element-271" /> : <Eye className="h-5 w-5" data-id="element-272" />}
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1" data-id="element-273">
                  <span className="w-1 h-1 rounded-full bg-red-500 inline-block" data-id="element-274" />
                  {errors.password}
                </p>}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between pt-1" data-id="element-275">
              <div className="flex items-center" data-id="element-276">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-slate-300 rounded cursor-pointer" data-id="element-277" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-600 cursor-pointer select-none" data-id="element-278">
                  Angemeldet bleiben
                </label>
              </div>
              <div className="text-sm" data-id="element-279">
                <a href="#" className="font-medium text-emerald-600 hover:text-emerald-500 transition-colors" onClick={e => e.preventDefault()} data-id="element-280">
                  Passwort vergessen?
                </a>
              </div>
            </div>

            {/* Submit Button */}
            <motion.div variants={itemVariants} className="pt-2" data-id="element-281">
              <button type="submit" disabled={isLoading} className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-70 disabled:cursor-not-allowed transition-all" data-id="element-282">
                {isLoading ? <>
                    <Loader2 className="animate-spin h-5 w-5" data-id="element-283" />
                    <span data-id="element-284">Wird angemeldet...</span>
                  </> : <>
                    <span data-id="element-285">Anmelden</span>
                    <ArrowRight className="h-4 w-4" data-id="element-286" />
                  </>}
              </button>
            </motion.div>
          </motion.form>

          {/* Divider */}
          <motion.div variants={itemVariants} className="mt-8" data-id="element-287">
            <div className="relative" data-id="element-288">
              <div className="absolute inset-0 flex items-center" data-id="element-289">
                <div className="w-full border-t border-slate-200" data-id="element-290" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-291">
                <span className="px-3 bg-white text-slate-500" data-id="element-292">oder</span>
              </div>
            </div>

            {/* Secondary Action */}
            <div className="mt-6" data-id="element-293">
              <button type="button" className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-slate-200 rounded-lg shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-200 transition-colors" data-id="element-294">
                Als Gast-Zugang anfordern
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>;
}